/**
 * Award type constants and helpers.
 * Centralises FE/HE award labels used across UI and validation.
 * @module utils/award-types
 */

export const FE_AWARD_TYPES = [
  "FE Major Award",
  "FE Component Award",
  "FE Minor Award",
  "FE Special Purpose Award",
  "FE Supplemental Award",
] as const;

export const HE_AWARD_TYPES = [
  "Higher Certificate",
  "Ordinary Bachelor Degree",
  "Honours Bachelor Degree",
  "Higher Diploma",
  "Postgraduate Diploma",
  "Masters",
  "Doctoral",
  "Micro-credential",
] as const;

export type FeAwardType = (typeof FE_AWARD_TYPES)[number];
export type HeAwardType = (typeof HE_AWARD_TYPES)[number];

const FE_AWARD_TYPES_SET = new Set<string>(FE_AWARD_TYPES);
const HE_AWARD_TYPES_SET = new Set<string>(HE_AWARD_TYPES);

export function isFeAwardType(awardType: string): boolean {
  return FE_AWARD_TYPES_SET.has((awardType ?? "").trim());
}

/**
 * What `count` of this award's credits are called: QQI FET credits on an FE
 * award, ECTS otherwise. ECTS reads the same for one; a FET credit does not.
 */
export function creditSystemName(
  awardType: string | undefined,
  count: number | string | null | undefined = undefined,
): string {
  if (!isFeAwardType(awardType ?? "")) {
    return "ECTS";
  }
  return Number(count) === 1 ? "QQI FET credit" : "QQI FET credits";
}

export function isHeAwardType(awardType: string): boolean {
  return HE_AWARD_TYPES_SET.has((awardType ?? "").trim());
}

/**
 * Whether a programme follows the QQI Component Award Scheme: an FE Major
 * Award always does, and another FE award does once it is linked to a CAS
 * certificate. A CAS code left on a higher education award is ignored.
 */
export function isCasProgramme(programme: {
  awardType?: string;
  casMajorAwardCode?: string;
}): boolean {
  const awardType = (programme.awardType ?? "").trim();
  if (!isFeAwardType(awardType)) {
    return false;
  }
  return awardType === "FE Major Award" || Boolean((programme.casMajorAwardCode ?? "").trim());
}

/**
 * How many programme outcomes to aim for: QQI's CAS guidance asks for four to
 * six, and six to twelve is the usual range everywhere else.
 */
export function outcomeBand(programme: { awardType?: string; casMajorAwardCode?: string }): {
  min: number;
  max: number;
} {
  return isCasProgramme(programme) ? { min: 4, max: 6 } : { min: 6, max: 12 };
}
